<template>
  <div v-if="environment === 'production'">
    <h1 class="title">{{ $t("customer.dashboard.title") }}</h1>
    <h3 class="pa-4">{{ $t("comingSoon") }}</h3>
  </div>
  <div v-else>
    <h1 class="title">{{ $t("customer.reports.title") }}</h1>
    <div v-if="userTimeZone">
      <v-container
        v-if="reportsDisplay"
        id="dashboard"
        fluid
        tag="section"
        justify="center"
      >
        <time-zone-picker
          ref="timeZonePickerComponent"
          alignment="end"
          :userTimeZone="userTimeZone"
          @change-timezone="submitTimeZone"
          @refresh-event="refreshEvent"
        ></time-zone-picker>
        <v-row class="summary-alignment" justify="left"
          ><h3>{{ $t("customer.reports.summaryTitle") }}</h3></v-row
        >
        <v-row class="mt-10" justify="center">
          <!-- Total Search Records Count  -->
          <v-col cols="12" sm="6" lg="3">
            <base-material-stats-card
              :color="statsCardUIElements.search.color"
              :icon="statsCardUIElements.search.icon"
              :title="$t('customer.reports.summaryDataPoints.searchTitle')"
              :value="todaySummaryMetrics.SEARCH_RECORD.toString()"
            />
          </v-col>
          <!-- Total Create Records Count -->
          <v-col cols="12" sm="6" lg="3">
            <base-material-stats-card
              :color="statsCardUIElements.create.color"
              :icon="statsCardUIElements.create.icon"
              :title="$t('customer.reports.summaryDataPoints.createTitle')"
              :value="todaySummaryMetrics.CREATE_RECORD.toString()"
            />
          </v-col>
          <!-- Total Update Records Count -->
          <v-col cols="12" sm="6" lg="3">
            <base-material-stats-card
              :color="statsCardUIElements.update.color"
              :icon="statsCardUIElements.update.icon"
              :title="$t('customer.reports.summaryDataPoints.updateTitle')"
              :value="todaySummaryMetrics.UPDATE_RECORD.toString()"
            />
          </v-col>
        </v-row>
        <v-row class="mt-10" justify="center">
          <!-- Total Link Transcripts Count -->
          <v-col cols="12" sm="6" lg="3">
            <base-material-stats-card
              :color="statsCardUIElements.link.color"
              :icon="statsCardUIElements.link.icon"
              :title="
                $t('customer.reports.summaryDataPoints.linkTranscriptTitle')
              "
              :value="todaySummaryMetrics.LINKTRANSCRIPT_RECORD.toString()"
            />
          </v-col>
          <!-- Total Agents Login Count -->
          <v-col cols="12" sm="6" lg="3">
            <base-material-stats-card
              :color="statsCardUIElements.login.color"
              :icon="statsCardUIElements.login.icon"
              :title="$t('customer.reports.summaryDataPoints.loginsTitle')"
              :value="todaySummaryMetrics.LOGIN_RECORD.toString()"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="3"></v-col>
        </v-row>
        <v-divider class="summary-alignment mt-5"></v-divider>
        <customer-filters
          ref="customerFiltersComponent"
          class="mt-5"
        ></customer-filters>
      </v-container>
    </div>
    <!-- TimeZone picker -->
    <div v-else>
      <v-dialog
        v-if="userTimeZone === ''"
        v-model="timeZonePickerForm"
        max-width="500px"
        persistent
      >
        <!-- Time Zone Picker Form -->
        <v-card height="100%">
          <v-card-title class="justify-center">
            <span class="headline">{{
              $t("customer.reports.timeZoneLabel")
            }}</span>
          </v-card-title>
          <v-card-subtitle>
            <span class="subtitle-2">
              ({{ $t("customer.reports.timeZoneHint") }})
            </span>
          </v-card-subtitle>
          <v-card-text>
            <v-form
              ref="timeZonePickerForm"
              v-model="timeZoneFormValidity"
              @submit.prevent="submitTimeZone"
              @change-timezone="submitTimeZone"
            >
              <v-container class="inputs-container">
                <time-zone-picker
                  ref="timeZonePickerComponent"
                  alignment="center"
                  :userTimeZone="userTimeZone"
                ></time-zone-picker>
              </v-container>
            </v-form>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn
              @click="submitTimeZone"
              :disabled="!timeZoneFormValidity"
              color="#173862"
              dark
              class="pa-0 ma-0"
              align="center"
            >
              {{ $t("customer.reports.saveButton") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import API from "../api/dashboardReports";
import { dashboardReportsMixins } from "../mixins/dashboardReportsMixins";
import { mapMutations } from "vuex";
import { authComputed } from "../store/helpers.js";

export default {
  name: "AdminReports",
  components: {
    "customer-filters": () =>
      import("@/components/DashboardStats/CustomerFilters.vue"),
    "base-material-stats-card": () =>
      import("@/components/DashboardStats/MaterialStatsCard.vue"),
    "time-zone-picker": () =>
      import("@/components/DashboardStats/TimeZonePicker.vue"),
  },
  computed: {
    ...authComputed,
  },
  async created() {
    // Hide page for production
    const hostname = window.location.href;
    const domainInfo = hostname.split("/");
    if (domainInfo[2] === "crmconnect.navomi.com") {
      // production
      this.environment = "production";
    } else {
      const storageData = JSON.parse(sessionStorage.getItem("user"));
      this.userTimeZone = storageData.timeZone ? storageData.timeZone : "";

      if (this.userTimeZone) {
        this.fetchTodaySummaryReports();
      } else {
        // Display a model to pick timeframe before procedding to stats
        this.timeZonePickerForm = true;
      }

      // Fetching stats icon colors
      this.statsCardUIElements = this.reportsCardsUIElements();
    }
  },
  data() {
    return {
      accountId: JSON.parse(sessionStorage.getItem("user")).accountId,
      environment: "",
      reportsDisplay: false,
      selectedFilters: {},
      statsCardUIElements: {},
      todaySummaryMetrics: {},
      timeZonePickerForm: false,
      timeZoneFormValidity: false,
      userTimeZone: "",
    };
  },
  methods: {
    async fetchTodaySummaryReports() {
      // Fetching todays summary reports
      const todaySummaryReports =
        this.$store.getters["users/getUsertodaySummaryReports"];

      if (Object.keys(todaySummaryReports).length) {
        this.todaySummaryMetrics = todaySummaryReports;
        this.reportsDisplay = true;
      } else {
        // API call to fetch all accounts reports

        // Fetching previous filters
        const customerReportsFilters =
          this.$store.getters["users/getCustomerReportsFilters"];
        this.selectedFilters = customerReportsFilters;
        // If no previous filters storeing modified filters
        if (Object.keys(customerReportsFilters).length === 0) {
          this.selectedFilters = {
            accountOption: this.accountId,
            timeFrameOption: "Today",
          };
          this.$store.commit(
            "users/SET_CUSTOMER_REPORTS_FILTERS",
            this.selectedFilters
          );
        }

        const payload = {
          accountId: this.accountId,
        };

        await API.fetchSummaryReports(payload)
          .then(async (response) => {
            if (response) {
              this.todaySummaryMetrics = response.value.data;
              this.reportsDisplay = true;

              // Store today's summary metrics
              this.$store.commit(
                "users/SET_USER_TODAY_SUMMARY_REPORTS",
                this.todaySummaryMetrics
              );
            }
          })
          .catch((error) => {
            console.error(
              `[CustomerReportsDashboard] - fetchSummaryReports - error ${JSON.stringify(
                error
              )}`
            );
            this.showSnack(
              `Fetching reports got failed! Please try again!`,
              true
            );
          });
      }
    },
    ...mapMutations({
      setSnack: "setSnack",
    }),
    async refreshEvent() {
      const todaySummaryReports =
        this.$store.getters["users/getUsertodaySummaryReports"];
      if (Object.keys(todaySummaryReports).length) {
        this.$store.commit("users/SET_USER_TODAY_SUMMARY_REPORTS", {});
        await this.fetchTodaySummaryReports();
        await this.$refs.customerFiltersComponent.filterChange();
        this.$refs.timeZonePickerComponent.refreshButtonStatus = false;
      }
    },
    async submitTimeZone() {
      this.userTimeZone = this.$refs.timeZonePickerComponent.seletedTimeZone;
      const userData = JSON.parse(sessionStorage.getItem("user"));
      const timeZoneName = this.userTimeZone.split(") ")[1];
      // Store new timezone value into DB
      let postObj = {
        strict: true,
        value: {
          op: "TIMEZONEUPDATE",
          id: userData.id,
          accountId: this.accountId,
          sessionId: userData.sessionId,
          data: {
            timeZone: timeZoneName,
          },
        },
      };
      // API call to save new timezone
      await API.saveTimeZone(postObj)
        .then(async (response) => {
          if (response) {
            // Stroing new timezone value in session storage
            let storageData = JSON.parse(sessionStorage.getItem("user"));
            storageData.timeZone = this.userTimeZone.split(") ")[1];
            sessionStorage.setItem("user", JSON.stringify(storageData));
            this.timeZonePickerForm = false;

            // Checking summary metrics existance when displaying timezone box first time
            const todaySummaryReports =
              this.$store.getters["users/getUsertodaySummaryReports"];
            if (Object.keys(todaySummaryReports).length) {
              this.$store.commit("users/SET_GRAPH_DATA", {});
              this.$refs.customerFiltersComponent.filterChange();
            } else {
              this.fetchTodaySummaryReports();
            }
          }
        })
        .catch((error) => {
          console.error(
            `[AdminReportsDashboard] - saving new timezone - error ${JSON.stringify(
              error
            )}`
          );
          this.showSnack(
            `Saving new timezone got failed! Please try again!`,
            true
          );
        });
    },
  },
  mixins: [dashboardReportsMixins],
};
</script>

<style>
.container--fluid {
  max-width: 96%;
  align-items: right;
}
.summary-alignment {
  margin-left: 14% !important;
}
</style>
